import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@staff-app/ui';
import { ChevronDown, ChevronRight, Globe2, Plus, Trash2 } from 'lucide-react';
import {
  INTL_CURRENCIES,
  INTL_CURRENCY_SYMBOLS,
  INTL_DEVICE_LABELS,
  INTL_DEVICE_MODELS,
  INTL_REQUEST_MAX_QUANTITY,
  STRIPE_CUSTOMER_ID_RE,
  intlCurrencyForDestination,
  type IntlAllRequest,
  type IntlCurrency,
  type IntlDeviceModel,
} from '@staff-app/shared-types';
// One country picker in the app — the Quote Generator's, as International Requests uses it. NOT
// restricted to Amazon's servable list: China and Houston ship where Amazon does not.
import { CountryCombobox } from '@/features/optihub/quote/components/CountryCombobox';
import { isKnownCountry, normalizeCountry } from '@/features/optihub/quote/countries';
import { countryName, useIntlStripeCustomer } from '@/features/intl-requests/hooks/useIntlRequests';
import { formLineProblems, toSubmitLines, type FormLine } from '../form-lines';
import { useIntlAllEligibleModels, useSubmitIntlAllRequest } from '../hooks/useIntlAllRequests';

/**
 * One labelled field — a copy of International Requests' (private there), so the two forms share
 * one look: `half` puts two on a row on wide screens, `hintTone="error"` paints a refusal red.
 */
function Field({
  label,
  hint,
  hintTone = 'muted',
  children,
  half,
}: {
  label: string;
  hint?: string;
  hintTone?: 'muted' | 'error';
  children: React.ReactNode;
  half?: boolean;
}) {
  return (
    <div className={half ? 'sm:col-span-1' : 'sm:col-span-2'}>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint ? (
        <p
          className={cn('mt-1 text-xs', hintTone === 'error' ? 'text-destructive' : 'text-muted-foreground')}
          data-tone={hintTone}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "2 × Pro Player, 4 × OptiStick" — what the filed banner says was asked for. */
function linesSummary(r: IntlAllRequest): string {
  return r.lines.map((l) => `${l.quantity} × ${INTL_DEVICE_LABELS[l.deviceModel] ?? l.deviceModel}`).join(', ');
}

/**
 * FILE A REQUEST — ROOT + SUPPORT. Laid out like International Requests' intake card on purpose
 * (Paul, 2026-10-28: "should be same as international request"): same order, labels, hints,
 * Stripe lookup + prefill, currency rules and shipping-address block, so a rep who knows one form
 * knows the other.
 *
 * WHAT DIFFERS, and why:
 *  - N DEVICE LINES instead of one Item + Quantity. The picker is the UNION of the Products List
 *    ticks ("Amazon MCF" and "China / Houston"): a model is offered if SOME route may ship it.
 *    Which route actually does is fulfilment's call, made in the Route dialog after filing.
 *  - ANY COUNTRY. IR lists only what Amazon can reach; China and Houston ship elsewhere too.
 *  - NO PRICE AFTER FILING. Freight is per shipment and the shipments do not exist until
 *    fulfilment routes the request, so the banner says what happens next instead of a total.
 *  - NO STRIPE BILLING-ADDRESS WARNING. Filing here does not write the customer's address.
 */
export function IntlAllRequestForm({ onFiled }: { onFiled?: (r: IntlAllRequest) => void }) {
  const [open, setOpen] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [company, setCompany] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [shipToCountry, setShipToCountry] = useState('');
  const [currency, setCurrency] = useState<IntlCurrency>('usd');
  const [shipToName, setShipToName] = useState('');
  const [shipToLine1, setShipToLine1] = useState('');
  const [shipToLine2, setShipToLine2] = useState('');
  const [shipToCity, setShipToCity] = useState('');
  const [shipToRegion, setShipToRegion] = useState('');
  const [shipToPostalCode, setShipToPostalCode] = useState('');
  const [shipToPhone, setShipToPhone] = useState('');
  const [lines, setLines] = useState<FormLine[]>([{ deviceModel: '', quantity: '' }]);
  const [filed, setFiled] = useState<IntlAllRequest | null>(null);
  /** Fields the rep typed in — a Stripe prefill or the recipient mirror never overwrites them. */
  const touched = useRef<Set<string>>(new Set());
  const markTouched = (field: string) => touched.current.add(field);
  /** Filing folds fifteen fields into a banner; this brings the banner back into view. */
  const cardRef = useRef<HTMLDivElement>(null);

  const eligibility = useIntlAllEligibleModels();
  const offered = useMemo(
    () => INTL_DEVICE_MODELS.filter((m) => eligibility.mcf.includes(m) || eligibility.shopify.includes(m)),
    [eligibility.mcf, eligibility.shopify],
  );

  // One device enabled → preselect it on an empty first line, as IR does; never over a pick.
  useEffect(() => {
    if (offered.length === 1 && lines.length === 1 && !lines[0].deviceModel) {
      setLines([{ ...lines[0], deviceModel: offered[0] }]);
    }
  }, [offered, lines]);

  // Prefill from Stripe, never over anything the rep typed (International Requests' rule).
  const { data: stripeData, loading: resolving, error: resolveError } = useIntlStripeCustomer(stripeCustomerId);
  const stripeCustomer = stripeData?.intlStripeCustomer;
  const lockedCurrency =
    stripeCustomer && !stripeCustomer.deleted ? (stripeCustomer.currency ?? undefined) : undefined;
  useEffect(() => {
    if (!stripeCustomer || stripeCustomer.deleted) return;
    const fill = (field: string, current: string, set: (v: string) => void, incoming?: string | null) => {
      if (!incoming || touched.current.has(field) || current.trim()) return;
      set(incoming);
    };
    fill('company', company, setCompany, stripeCustomer.name);
    fill('contactEmail', contactEmail, setContactEmail, stripeCustomer.email);
    fill('contactName', contactName, setContactName, stripeCustomer.contactName);
    fill('vatNumber', vatNumber, setVatNumber, stripeCustomer.vatNumber);
  }, [stripeCustomer, company, contactEmail, contactName, vatNumber]);

  // The recipient follows the contact name until the rep types in it (IR's mirror).
  useEffect(() => {
    if (touched.current.has('shipToName')) return;
    setShipToName(contactName);
  }, [contactName]);

  useEffect(() => {
    if (!filed) return;
    cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [filed]);

  // The currency follows the destination, as on IR; the rep may still change it afterwards.
  const suggestedCurrency = intlCurrencyForDestination(normalizeCountry(shipToCountry));
  useEffect(() => {
    if (suggestedCurrency) setCurrency(suggestedCurrency);
  }, [suggestedCurrency]);
  const currencyMismatch = Boolean(suggestedCurrency) && currency !== suggestedCurrency;

  const [submit, { loading, error }] = useSubmitIntlAllRequest();

  const customerOk = STRIPE_CUSTOMER_ID_RE.test(stripeCustomerId.trim());
  const emailOk = EMAIL_RE.test(contactEmail.trim());
  const countryOk = isKnownCountry(shipToCountry);
  const vatRequired = normalizeCountry(shipToCountry)?.toUpperCase() !== 'US';
  const vatOk = !vatRequired || vatNumber.trim().length > 0;
  const recipientOk = shipToName.trim().length > 0;
  const streetOk = shipToLine1.trim().length > 0;
  const phoneOk = shipToPhone.trim().length > 0;
  const lineProblems = formLineProblems(lines);
  const canFile =
    customerOk && emailOk && countryOk && vatOk && recipientOk && streetOk && phoneOk && !lineProblems.length;

  const setLine = (i: number, patch: Partial<FormLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const reset = () => {
    touched.current = new Set();
    for (const set of [
      setStripeCustomerId, setContactEmail, setContactName, setCompany, setVatNumber, setShipToCountry,
      setShipToName, setShipToLine1, setShipToLine2, setShipToCity, setShipToRegion, setShipToPostalCode,
      setShipToPhone,
    ]) {
      set('');
    }
    setCurrency('usd');
    setLines([{ deviceModel: '', quantity: '' }]);
  };

  const onSubmit = async () => {
    if (!canFile) return;
    const input: Record<string, unknown> = {
      stripeCustomerId: stripeCustomerId.trim(),
      contactEmail: contactEmail.trim(),
      shipToCountry: normalizeCountry(shipToCountry),
      currency,
      lines: toSubmitLines(lines),
    };
    for (const [key, value] of [
      ['contactName', contactName], ['company', company], ['vatNumber', vatNumber],
      ['shipToName', shipToName], ['shipToLine1', shipToLine1], ['shipToLine2', shipToLine2],
      ['shipToCity', shipToCity], ['shipToRegion', shipToRegion], ['shipToPostalCode', shipToPostalCode],
      ['shipToPhone', shipToPhone],
    ] as const) {
      if (value.trim()) input[key] = value.trim();
    }
    try {
      const res = await submit({ variables: { input: input as never } });
      const created = res.data?.submitIntlAllRequest;
      if (created) {
        setFiled(created);
        reset();
        setOpen(false);
        onFiled?.(created);
      }
    } catch {
      // Rendered from `error` below; the form keeps what was typed.
    }
  };

  return (
    <div ref={cardRef} className="rounded-lg border bg-panel" data-testid="air-form">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        data-testid="air-form-toggle"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <Globe2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{'File an international request'}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {open ? '' : 'Stripe customer · destination · devices'}
        </span>
      </button>

      {filed && !open ? (
        <div className="border-t px-4 py-3 text-sm text-foreground" data-testid="air-form-filed">
          {`Filed ${filed.requestNo} — ${linesSummary(filed)} to ${countryName(filed.shipToCountry)}.`}
          <span className="text-muted-foreground">
            {' Fulfilment will route it; quote or invoice once it is routed. Nothing has been billed yet.'}
          </span>
        </div>
      ) : null}

      {open ? (
        <div className="border-t p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Stripe customer ID *"
              half
              hint={stripeCustomerId && !customerOk ? 'Should look like cus_ABC123.' : undefined}
            >
              <Input
                value={stripeCustomerId}
                onChange={(e) => setStripeCustomerId(e.target.value)}
                placeholder="cus_…"
                data-testid="air-form-customer"
                aria-invalid={Boolean(stripeCustomerId) && !customerOk}
              />
              {resolving ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="air-form-customer-resolving">
                  {'Looking up…'}
                </p>
              ) : stripeCustomer?.deleted ? (
                <p className="mt-1 text-xs text-destructive" data-testid="air-form-customer-deleted">
                  {'That customer is DELETED in Stripe — nothing was prefilled. Use a different customer.'}
                </p>
              ) : stripeCustomer ? (
                <p className="mt-1 text-xs text-success" data-testid="air-form-customer-found">
                  {stripeCustomer.name || stripeCustomer.email || stripeCustomer.id}
                </p>
              ) : resolveError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="air-form-customer-error">
                  {`${resolveError.message} — you can still type the fields by hand.`}
                </p>
              ) : null}
            </Field>

            <Field
              label={`Customer VAT number ${vatRequired ? '*' : ''}`.trim()}
              half
              hint={
                vatNumber.trim()
                  ? 'Saved to the Stripe customer as a tax ID when you file.'
                  : vatRequired
                    ? 'Please enter it if the shipping location is the EU or the UK.'
                    : 'Not required for a US destination — the US has no VAT.'
              }
            >
              <Input
                value={vatNumber}
                onChange={(e) => {
                  markTouched('vatNumber');
                  setVatNumber(e.target.value);
                }}
                placeholder="GB123456789"
                data-testid="air-form-vat"
              />
            </Field>

            <Field label="Contact email *" half hint={contactEmail && !emailOk ? 'Not an email address.' : undefined}>
              <Input
                value={contactEmail}
                onChange={(e) => {
                  markTouched('contactEmail');
                  setContactEmail(e.target.value);
                }}
                placeholder="buyer@example.com"
                data-testid="air-form-email"
                aria-invalid={Boolean(contactEmail) && !emailOk}
              />
            </Field>

            <Field label="Contact name" half>
              <Input
                value={contactName}
                onChange={(e) => {
                  markTouched('contactName');
                  setContactName(e.target.value);
                }}
                data-testid="air-form-contact-name"
              />
            </Field>

            <Field label="Company" half>
              <Input
                value={company}
                onChange={(e) => {
                  markTouched('company');
                  setCompany(e.target.value);
                }}
                data-testid="air-form-company"
              />
            </Field>

            <Field label="Invoice currency *" half>
              <Select value={currency} onValueChange={(v) => setCurrency(v as IntlCurrency)}>
                <SelectTrigger data-testid="air-form-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTL_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {`${INTL_CURRENCY_SYMBOLS[c]} ${c.toUpperCase()}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Advisory, as on IR: only an AMAZON MCF shipment prices in the marketplace's
                  currency; China / Houston freight is a USD table converted when rates exist. */}
              {currencyMismatch ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="air-form-currency-destination-warning">
                  {`If any of it ships by Amazon MCF, ${countryName(normalizeCountry(shipToCountry) ?? shipToCountry)} is priced by a marketplace that bills in `}
                  <span className="font-medium">{suggestedCurrency?.toUpperCase()}</span>
                  {`, so that freight may need entering by hand in ${currency.toUpperCase()}.`}
                </p>
              ) : null}
              {lockedCurrency && lockedCurrency !== currency ? (
                <p
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-muted-foreground"
                  data-testid="air-form-currency-locked-warning"
                >
                  {/* Business wording (Paul, 2026-10-28): Stripe's rule, not ours — and what still works. */}
                  <span className="font-medium text-foreground">
                    {`You can invoice this customer in ${currency.toUpperCase()}, but not quote them in it.`}
                  </span>
                  {` Stripe fixed their currency to ${lockedCurrency.toUpperCase()} when they were first billed, and Stripe only allows quotes in that currency — this is a Stripe rule, not a staff-app limit. If they need a quote, file the request in ${lockedCurrency.toUpperCase()}.`}
                </p>
              ) : null}
            </Field>

            {/* ── THE DEVICE LINES — IR's Item * + Quantity * pair, once per model ─────────── */}
            <div className="space-y-3 sm:col-span-2" data-testid="air-form-lines">
              {lines.map((l, i) => {
                const qty = Number(l.quantity);
                const qtyBad = Boolean(l.quantity) && (!Number.isInteger(qty) || qty < 1 || qty > INTL_REQUEST_MAX_QUANTITY);
                const taken = new Set(lines.filter((_, j) => j !== i).map((o) => o.deviceModel));
                return (
                  <div key={i} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={`air-form-line-${i}`}>
                    <Field
                      label={lines.length > 1 ? `Item ${i + 1} *` : 'Item *'}
                      half
                      hint={
                        i > 0
                          ? undefined
                          : eligibility.loading
                            ? 'Loading the devices enabled on the Products List…'
                            : offered.length
                              ? undefined
                              : 'No device is enabled for international orders.'
                      }
                    >
                      <Select
                        value={l.deviceModel || undefined}
                        onValueChange={(v) => setLine(i, { deviceModel: v as IntlDeviceModel })}
                        disabled={!offered.length}
                      >
                        <SelectTrigger data-testid={`air-form-model-${i}`}>
                          <SelectValue placeholder="Select a device" />
                        </SelectTrigger>
                        <SelectContent>
                          {offered.map((m) => (
                            <SelectItem key={m} value={m} disabled={taken.has(m)}>
                              {INTL_DEVICE_LABELS[m]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {i === 0 && !eligibility.loading && !offered.length ? (
                        <p className="mt-1 text-xs text-destructive" data-testid="air-form-no-models">
                          {'Nothing can be ordered until a device is enabled — tick "Amazon MCF" or '}
                          {'"China / Houston" for it on the Products List page.'}
                        </p>
                      ) : null}
                    </Field>
                    <Field
                      label="Quantity *"
                      half
                      hintTone={qtyBad ? 'error' : 'muted'}
                      hint={qtyBad ? `A whole number, 1 to ${INTL_REQUEST_MAX_QUANTITY}.` : undefined}
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          value={l.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value.replace(/[^\d]/g, '') })}
                          inputMode="numeric"
                          placeholder="600"
                          data-testid={`air-form-qty-${i}`}
                          aria-invalid={qtyBad}
                        />
                        {lines.length > 1 ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove line ${i + 1}`}
                            onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                            data-testid={`air-form-remove-${i}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </Field>
                  </div>
                );
              })}
              {lines.length < offered.length ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLines((ls) => [...ls, { deviceModel: '', quantity: '' }])}
                  data-testid="air-form-add-line"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {'Add another device'}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 border-t pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{'Shipping address'}</p>

            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Ship-to country *"
                half
                hintTone={shipToCountry && !countryOk ? 'error' : 'muted'}
                hint={shipToCountry && !countryOk ? 'Not a country we recognise — pick one from the list.' : undefined}
              >
                <CountryCombobox value={shipToCountry} onChange={setShipToCountry} testId="air-form-country" />
              </Field>
              <Field
                label="Recipient name *"
                half
                hint={
                  !shipToName.trim()
                    ? undefined
                    : touched.current.has('shipToName')
                      ? 'Who a courier asks for at the door.'
                      : 'Follows the contact name — type here if the recipient is someone else.'
                }
              >
                <Input
                  value={shipToName}
                  onChange={(e) => {
                    markTouched('shipToName');
                    setShipToName(e.target.value);
                  }}
                  data-testid="air-form-ship-name"
                />
              </Field>
              <Field label="Address line 1 *">
                <Input value={shipToLine1} onChange={(e) => setShipToLine1(e.target.value)} data-testid="air-form-line1" />
              </Field>
              <Field label="Address line 2">
                <Input value={shipToLine2} onChange={(e) => setShipToLine2(e.target.value)} data-testid="air-form-line2" />
              </Field>
              <Field label="City" half>
                <Input value={shipToCity} onChange={(e) => setShipToCity(e.target.value)} data-testid="air-form-city" />
              </Field>
              <Field label="Region / state" half>
                <Input value={shipToRegion} onChange={(e) => setShipToRegion(e.target.value)} data-testid="air-form-region" />
              </Field>
              <Field label="Postal code" half>
                <Input
                  value={shipToPostalCode}
                  onChange={(e) => setShipToPostalCode(e.target.value)}
                  data-testid="air-form-postal"
                />
              </Field>
              <Field label="Phone *" half>
                <Input
                  value={shipToPhone}
                  onChange={(e) => setShipToPhone(e.target.value)}
                  data-testid="air-form-phone"
                  aria-invalid={!phoneOk && Boolean(shipToCountry)}
                />
              </Field>
            </div>
          </div>

          {error ? (
            <p className="mt-4 text-sm text-destructive" data-testid="air-form-error">
              {error.message}
            </p>
          ) : null}

          <div className="mt-4 flex items-center gap-3">
            <Button onClick={onSubmit} disabled={!canFile || loading} data-testid="air-form-submit">
              {loading ? 'Filing…' : 'File request'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              disabled={loading}
              data-testid="air-form-cancel"
            >
              {'Cancel'}
            </Button>
            <span className="text-xs text-muted-foreground">{'You are recorded as the submitter.'}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
